import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import DieteManagerModal from "@/components/DieteManagerModal";

function todayDmy() {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}

function dmyToIso(dmy) {
  if (!dmy || !dmy.includes("/")) return "";
  const [d, m, y] = dmy.split("/");
  return `${y}-${m}-${d}`;
}

function isoToDmy(iso) {
  if (!iso || !iso.includes("-")) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

const GIORNI_IT = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];
function dmyWeekday(dmy) {
  if (!dmy || !dmy.includes("/")) return "";
  const [d, m, y] = dmy.split("/").map(Number);
  if (!d || !m || !y) return "";
  const dt = new Date(y, m - 1, d);
  if (isNaN(dt.getTime())) return "";
  return GIORNI_IT[dt.getDay()];
}

export default function MensaGiornoPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importReport, setImportReport] = useState(null);
  const [selDate, setSelDate] = useState("");
  const [availDates, setAvailDates] = useState([]);
  const [filterPlesso, setFilterPlesso] = useState(() => new Set());
  const [showPlessoDD, setShowPlessoDD] = useState(false);
  const [filterClasse, setFilterClasse] = useState("");
  const [filterDieta, setFilterDieta] = useState("");
  const [filterRefettorio, setFilterRefettorio] = useState("");
  const [search, setSearch] = useState("");
  const fileRef = useRef(null);
  const [lastFileName, setLastFileName] = useState("");
  // 🆕 Diete/intolleranze gestite (config editabile)
  const [dieteList, setDieteList] = useState([]);
  const [dieteModalOpen, setDieteModalOpen] = useState(false);
  // 🆕 Selezione refettori per la stampa "elenchi spunta"
  const [spuntaRefs, setSpuntaRefs] = useState([]);
  const [selSpuntaRefs, setSelSpuntaRefs] = useState(() => new Set());
  const [showSpuntaDD, setShowSpuntaDD] = useState(false);
  // 🆕 Modale scelta ordinamento prima della stampa "+ deleghe"
  const [showOrdinaDeleghe, setShowOrdinaDeleghe] = useState(false);
  // 🆕 Modale scelta ordinamento prima della stampa "spunta"
  const [showOrdinaSpunta, setShowOrdinaSpunta] = useState(false);
  // 🆕 Salto pagina ad ogni Settore/Classe (solo stampa + deleghe, ordine Settore·Classe)
  const [saltoPagina, setSaltoPagina] = useState(true);
  // 🆕 Giu 2026: due turni per giorno (Ore 10 / Ore 12)
  const [curSlot, setCurSlot] = useState("Ore 12");
  const [importSlot, setImportSlot] = useState("Ore 12");
  const [slotSummary, setSlotSummary] = useState([]);
  const [showConfronto, setShowConfronto] = useState(false);
  const [confronto, setConfronto] = useState(null);

  const loadDiete = async () => {
    try {
      const r = await api.get("/mensa/giorno/diete");
      setDieteList(r.data?.items || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadDiete(); }, []);

  // 🆕 Carica i refettori disponibili per la spunta del giorno caricato (default: tutti selezionati)
  useEffect(() => {
    if (!data?.data || !data?.total) { setSpuntaRefs([]); setSelSpuntaRefs(new Set()); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/mensa/refettori/giorno/elenchi-spunta-refettori", { params: { data: data.data } });
        if (cancelled) return;
        const items = r.data?.refettori || [];
        setSpuntaRefs(items);
        setSelSpuntaRefs(new Set(items));
      } catch { if (!cancelled) { setSpuntaRefs([]); setSelSpuntaRefs(new Set()); } }
    })();
    return () => { cancelled = true; };
  }, [data?.data, data?.total]);

  const toggleSpuntaRef = (name) => {
    setSelSpuntaRefs(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const stampaElenchiSpunta = async (conDeleghe = false, ordina = null, saltoPag = null) => {
    const dataStr = data.data;
    const sel = Array.from(selSpuntaRefs);
    if (spuntaRefs.length > 0 && sel.length === 0) {
      alert("Seleziona almeno un refettorio da stampare.");
      return;
    }
    try {
      const params = { data: dataStr, slot: curSlot };
      if (spuntaRefs.length > 0 && sel.length < spuntaRefs.length) params.refettori = sel.join("|");
      if (conDeleghe) params.con_deleghe = true;
      if (ordina) params.ordina = ordina;
      if (conDeleghe && ordina === "settore_classe") params.salto_pagina = !!saltoPag;
      const r = await api.get("/mensa/refettori/giorno/elenchi-spunta-pdf",
        { params, responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, "_blank");
      if (!w) {
        const a = document.createElement("a");
        a.href = url;
        a.download = `${conDeleghe ? "elenchi_spunta_deleghe" : "elenchi_spunta"}_${dataStr.replace(/\//g, "-")}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      setShowSpuntaDD(false);
    } catch (e) {
      alert("Errore generazione PDF: " + (e.response?.data?.detail || e.message));
    }
  };

  const saveDietaCella = async (row, nuovaDieta) => {
    if ((row.dieta || "") === (nuovaDieta || "")) return;
    try {
      await api.put("/mensa/giorno/presenza/dieta", {
        data_iso: row.data_iso || dmyToIso(selDate),
        codice_fiscale: row.codice_fiscale,
        dieta: nuovaDieta || "",
      });
      // Refresh minimo: aggiorno la riga in memoria
      setData(d => d ? { ...d, rows: (d.rows || []).map(r => (r.codice_fiscale === row.codice_fiscale && (r.data_iso === row.data_iso)) ? { ...r, dieta: nuovaDieta } : r) } : d);
    } catch (e) {
      alert(`Errore salvataggio dieta: ${e.response?.data?.detail || e.message}`);
    }
  };

  const loadDates = async () => {
    try {
      const r = await api.get("/mensa/giorno/dates");
      setAvailDates(r.data.dates || []);
      if (!selDate && r.data.dates?.length) setSelDate(r.data.dates[0].data);
    } catch { /* ignore */ }
  };

  const loadSlotSummary = async (dateDmy) => {
    if (!dateDmy) { setSlotSummary([]); return; }
    try {
      const r = await api.get("/mensa/giorno/slots", { params: { data: dateDmy } });
      setSlotSummary(r.data.slots || []);
    } catch { setSlotSummary([]); }
  };

  const carica = async (dateDmy, slotArg) => {
    setLoading(true); setErr(null);
    const slotToUse = slotArg || curSlot;
    try {
      const params = { slot: slotToUse };
      if (dateDmy) params.data = dateDmy;
      const r = await api.get("/mensa/giorno", { params });
      setData(r.data);
      if (r.data?.data && r.data.data !== selDate) setSelDate(r.data.data);
      await loadSlotSummary(r.data?.data || dateDmy);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento presenze");
      setData(null);
    } finally { setLoading(false); }
  };

  const cambiaSlot = async (s) => {
    setCurSlot(s);
    await carica(data?.data || selDate || undefined, s);
  };

  const apriConfronto = async () => {
    if (!data?.data) return;
    try {
      const r = await api.get("/mensa/giorno/confronto", { params: { data: data.data } });
      setConfronto(r.data);
      setShowConfronto(true);
    } catch (e) {
      alert("Errore confronto: " + (e.response?.data?.detail || e.message));
    }
  };

  useEffect(() => { loadDates().then(() => carica()); /* eslint-disable-next-line */ }, []);

  const [storicizzando, setStoricizzando] = useState(false);
  const storicizza = async () => {
    if (!data?.data) return;
    if (!window.confirm(`Storicizzare la comparazione delle rilevazioni Ore 10 / Ore 12 del ${data.data}?\nVerrà sovrascritta l'eventuale storicizzazione precedente per la stessa data.`)) return;
    setStoricizzando(true);
    try {
      const r = await api.post("/mensa/giorno/storicizza", null, { params: { data: data.data } });
      alert(`✓ Storicizzato ${r.data.data}\nOre 10: ${r.data.tot_ore10}\nOre 12: ${r.data.tot_ore12}\nDifferenza: ${r.data.delta >= 0 ? "+" : ""}${r.data.delta}\n\nVai in "📚 Storico Mensa" per consultare e indicare la differenza comunicata alla mensa.`);
    } catch (e) { setErr(e.response?.data?.detail || "Errore storicizzazione"); }
    finally { setStoricizzando(false); }
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setLastFileName(f.name);
    setImporting(true); setErr(null); setImportReport(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("slot", importSlot);
      const r = await api.post("/mensa/giorno/import-xls", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportReport(r.data);
      await loadDates();
      const targetDate = r.data?.dates?.[0] ? isoToDmy(r.data.dates[0]) : null;
      if (targetDate) setSelDate(targetDate);
      setCurSlot(importSlot);
      await carica(targetDate || undefined, importSlot);
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Errore durante l'importazione");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const avviaImport = (slot) => {
    setImportSlot(slot);
    setTimeout(() => fileRef.current && fileRef.current.click(), 0);
  };

  const handleDeleteDay = async () => {
    if (!data?.data) return;
    if (!window.confirm(`Eliminare TUTTE le presenze del ${data.data}?`)) return;
    try {
      await api.delete("/mensa/giorno", { params: { data: data.data } });
      await loadDates();
      await carica();
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore eliminazione");
    }
  };

  const filtered = useMemo(() => {
    const rows = data?.rows || [];
    const s = search.trim().toLowerCase();
    return rows.filter(r => {
      if (filterPlesso.size > 0 && !filterPlesso.has(r.plesso)) return false;
      if (filterClasse && r.classe_desc !== filterClasse) return false;
      if (filterDieta && (r.dieta || "") !== filterDieta) return false;
      if (filterRefettorio && (r.refettorio_calc || "") !== filterRefettorio) return false;
      if (s) {
        const hay = `${r.studente || ""} ${r.codice_fiscale || ""} ${r.docente || ""}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [data, filterPlesso, filterClasse, filterDieta, filterRefettorio, search]);

  const opzioniPlesso = useMemo(() => {
    const set = new Set((data?.rows || []).map(r => r.plesso).filter(Boolean));
    return Array.from(set).sort();
  }, [data]);
  // Raggruppa i plessi per settore (4° carattere del codice plesso) per il dropdown multi-select
  const plessiPerSettore = useMemo(() => {
    const SETT_ORDER = ["A", "E", "F", "M", "S", "N", "I"];
    const SETT_LABEL = { A: "INFANZIA", E: "PRIMARIA E", F: "PRIMARIA F", M: "SECONDARIA", S: "SECONDARIA S", N: "INFANZIA N", I: "INFANZIA I" };
    const groups = {};
    for (const p of opzioniPlesso) {
      const s = (p || "").charAt(3).toUpperCase() || "?";
      if (!groups[s]) groups[s] = [];
      groups[s].push(p);
    }
    const order = [...SETT_ORDER, ...Object.keys(groups).filter(k => !SETT_ORDER.includes(k)).sort()];
    return order.filter(k => groups[k]).map(k => ({
      settore: k,
      label: SETT_LABEL[k] || `SETTORE ${k}`,
      plessi: groups[k].sort(),
    }));
  }, [opzioniPlesso]);
  const opzioniClasse = useMemo(() => {
    const set = new Set((data?.rows || []).map(r => r.classe_desc).filter(Boolean));
    return Array.from(set).sort();
  }, [data]);
  const opzioniDieta = useMemo(() => {
    const set = new Set((data?.rows || []).map(r => (r.dieta || "").trim()).filter(Boolean));
    return Array.from(set).sort();
  }, [data]);
  const opzioniRefettorio = useMemo(() => {
    const set = new Set((data?.rows || []).map(r => (r.refettorio_calc || "").trim()).filter(Boolean));
    return Array.from(set).sort();
  }, [data]);

  const grouped = useMemo(() => {
    const SETT_ORDER = ["A", "E", "F", "M", "S", "N", "I"];
    const settoreOf = (plesso) => (plesso || "").charAt(3).toUpperCase();
    const settKey = (p) => {
      const s = settoreOf(p);
      const i = SETT_ORDER.indexOf(s);
      return i >= 0 ? i : 100;
    };
    const rows = [...filtered].sort((a, b) => {
      const sa = settKey(a.plesso), sb = settKey(b.plesso);
      if (sa !== sb) return sa - sb;
      return (a.plesso || "").localeCompare(b.plesso || "", "it") ||
        (a.classe_desc || "").localeCompare(b.classe_desc || "", "it") ||
        (a.studente || "").localeCompare(b.studente || "", "it");
    });
    const out = [];
    let curPlesso = null, curClasse = null;
    let plessoCount = 0, classeCount = 0;
    const flushClasse = () => {
      if (curClasse !== null) out.push({ type: "classe_sub", plesso: curPlesso, classe: curClasse, count: classeCount });
    };
    const flushPlesso = () => {
      if (curPlesso !== null) out.push({ type: "plesso_sub", plesso: curPlesso, count: plessoCount });
    };
    for (const r of rows) {
      const p = r.plesso || "";
      const c = r.classe_desc || "";
      if (p !== curPlesso) {
        flushClasse();
        flushPlesso();
        curPlesso = p; curClasse = c;
        plessoCount = 0; classeCount = 0;
      } else if (c !== curClasse) {
        flushClasse();
        curClasse = c; classeCount = 0;
      }
      out.push({ type: "row", r });
      classeCount += 1;
      plessoCount += 1;
    }
    flushClasse();
    flushPlesso();
    return out;
  }, [filtered]);

  return (
    <div>
      <div style={{ margin: "8px 20px", background: "white", border: "1px solid #ccc", padding: 12, display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#666", marginBottom: 3 }}>DATA</div>
          <select data-testid="mg-date-select" value={data?.data_iso || ""}
            onChange={e => { const iso = e.target.value; setSelDate(isoToDmy(iso)); carica(isoToDmy(iso)); }}
            style={{ padding: "5px 10px", border: "1px solid #ccc", fontSize: 12, minWidth: 200 }}>
            {(!availDates || availDates.length === 0) && <option value="">—</option>}
            {availDates.map(d => (
              <option key={d.data_iso} value={d.data_iso}>{dmyWeekday(d.data)} {d.data} · {d.total} presenti</option>
            ))}
          </select>
        </div>

        <label data-testid="mg-import-ore10-btn" onClick={() => avviaImport("Ore 10")}
          style={{ padding: "6px 14px", background: importing ? "#90a4ae" : "#1565c0", color: "white", fontSize: 12, fontWeight: 700, cursor: importing ? "wait" : "pointer", border: "none", borderRadius: 3, display: "inline-flex", alignItems: "center", gap: 6 }}>
          {importing && importSlot === "Ore 10" ? "Importazione..." : "📥 Importa Ore 10"}
        </label>
        <label data-testid="mg-import-ore12-btn" onClick={() => avviaImport("Ore 12")}
          style={{ padding: "6px 14px", background: importing ? "#90a4ae" : "#2e7d32", color: "white", fontSize: 12, fontWeight: 700, cursor: importing ? "wait" : "pointer", border: "none", borderRadius: 3, display: "inline-flex", alignItems: "center", gap: 6 }}>
          {importing && importSlot === "Ore 12" ? "Importazione..." : "📥 Importa Ore 12"}
        </label>
        <input ref={fileRef} type="file" accept=".xls,.xlsx,.xml" onChange={handleFile} disabled={importing}
          style={{ display: "none" }} data-testid="mg-file-input" />
        {lastFileName && (
          <span data-testid="mg-last-file" title={lastFileName}
            style={{ fontSize: 11, color: "#2e7d32", fontWeight: 700, fontFamily: "Consolas, monospace", maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            📄 {lastFileName}
          </span>
        )}

        {data?.data && data?.total > 0 && (
          <button onClick={async () => {
              const dataStr = data.data;
              if (!window.confirm(
                `Stampa report refettori per il giorno:\n\n   ${dataStr}\n\n` +
                `(${data.total} alunni rilevati)\n\n` +
                `Confermi che è la data corretta?`
              )) return;
              try {
                const r = await api.get("/mensa/refettori/giorno/pdf", { params: { data: dataStr, slot: curSlot }, responseType: "blob" });
                const blob = new Blob([r.data], { type: "application/pdf" });
                const url = URL.createObjectURL(blob);
                const w = window.open(url, "_blank");
                if (!w) {
                  // popup blocker → fallback download
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `report_refettori_${dataStr.replace(/\//g, "-")}.pdf`;
                  document.body.appendChild(a); a.click(); a.remove();
                }
                setTimeout(() => URL.revokeObjectURL(url), 30000);
              } catch (e) {
                alert("Errore generazione PDF: " + (e.response?.data?.detail || e.message));
              }
            }}
            data-testid="mg-print-report"
            title={`Stampa report refettori (PDF) per ${data.data} sui presenti reali del giorno`}
            style={{ padding: "6px 14px", background: "#c62828", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer", display: "inline-flex", flexDirection: "column", alignItems: "center", lineHeight: 1.1 }}>
            <span>🖨 Stampa report refettori</span>
            <span style={{ fontSize: 10, fontWeight: 400, opacity: 0.95 }}>per il <b>{dmyWeekday(data.data)} {data.data}</b></span>
          </button>
        )}

        {data?.data && data?.total > 0 && (
          <div data-testid="mg-spunta-wrapper" style={{ position: "relative", display: "inline-block" }}>
            <button onClick={() => setShowSpuntaDD(s => !s)}
              data-testid="mg-print-elenchi-spunta"
              title={`Scegli i refettori e stampa gli elenchi di spunta per ${data.data}`}
              style={{ padding: "6px 14px", background: "#0d47a1", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer", display: "inline-flex", flexDirection: "column", alignItems: "center", lineHeight: 1.1 }}>
              <span>📋 Stampa elenchi spunta {showSpuntaDD ? "▲" : "▼"}</span>
              <span style={{ fontSize: 10, fontWeight: 400, opacity: 0.95 }}>
                {spuntaRefs.length === 0 ? "nessun refettorio" : `${selSpuntaRefs.size}/${spuntaRefs.length} refettori selezionati`}
              </span>
            </button>
            {showSpuntaDD && (
              <>
                <div onClick={() => setShowSpuntaDD(false)} style={{ position: "fixed", inset: 0, zIndex: 50 }} />
                <div data-testid="mg-spunta-dropdown"
                  style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, background: "white", border: "1px solid #ccc", borderTop: "3px solid #0d47a1", boxShadow: "0 6px 16px rgba(0,0,0,0.15)", zIndex: 60, minWidth: 280, maxHeight: 420, overflow: "auto", padding: "4px 0" }}>
                  <div style={{ padding: "6px 10px", fontSize: 11, fontWeight: 700, color: "#0d47a1", borderBottom: "1px solid #eee", background: "#fafafa" }}>
                    Refettori da stampare
                  </div>
                  <div style={{ display: "flex", gap: 4, padding: "4px 8px", borderBottom: "1px solid #eee", background: "#fafafa" }}>
                    <button onClick={() => setSelSpuntaRefs(new Set(spuntaRefs))} data-testid="mg-spunta-all"
                      style={{ flex: 1, padding: "3px 8px", fontSize: 10, fontWeight: 700, background: "white", border: "1px solid #0d47a1", color: "#0d47a1", cursor: "pointer" }}>Tutti</button>
                    <button onClick={() => setSelSpuntaRefs(new Set())} data-testid="mg-spunta-none"
                      style={{ flex: 1, padding: "3px 8px", fontSize: 10, fontWeight: 700, background: "white", border: "1px solid #c62828", color: "#c62828", cursor: "pointer" }}>Nessuno</button>
                  </div>
                  {spuntaRefs.length === 0 && (
                    <div style={{ padding: "10px", fontSize: 11, color: "#888" }}>Nessun refettorio configurato per questo giorno.</div>
                  )}
                  {spuntaRefs.map(nome => (
                    <label key={nome} data-testid={`mg-spunta-ref-${nome}`}
                      style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 12px", fontSize: 11, cursor: "pointer", color: selSpuntaRefs.has(nome) ? "#0d47a1" : "#444", fontWeight: selSpuntaRefs.has(nome) ? 700 : 400 }}
                      onMouseEnter={e => e.currentTarget.style.background = "#f0f6ff"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <input type="checkbox" checked={selSpuntaRefs.has(nome)} onChange={() => toggleSpuntaRef(nome)} style={{ cursor: "pointer" }} />
                      <span>{nome}</span>
                    </label>
                  ))}
                  <div style={{ padding: "8px 10px", borderTop: "1px solid #eee", background: "#fafafa", display: "flex", flexDirection: "column", gap: 6 }}>
                    <button onClick={() => { setShowSpuntaDD(false); setShowOrdinaSpunta(true); }} data-testid="mg-spunta-print"
                      disabled={spuntaRefs.length > 0 && selSpuntaRefs.size === 0}
                      title="Elenco spunta. Prima di stampare ti verrà chiesto l'ordinamento."
                      style={{ width: "100%", padding: "7px 10px", background: (spuntaRefs.length > 0 && selSpuntaRefs.size === 0) ? "#9e9e9e" : "#0d47a1", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: (spuntaRefs.length > 0 && selSpuntaRefs.size === 0) ? "not-allowed" : "pointer" }}>
                      🖨 Stampa spunta {selSpuntaRefs.size > 0 ? `(${selSpuntaRefs.size})` : ""}
                    </button>
                    <button onClick={() => { setShowSpuntaDD(false); setShowOrdinaDeleghe(true); }} data-testid="mg-spunta-print-deleghe"
                      disabled={spuntaRefs.length > 0 && selSpuntaRefs.size === 0}
                      title="Elenco spunta orizzontale con la colonna dei delegati al ritiro (solo Primaria/Infanzia). Prima di stampare ti verrà chiesto l'ordinamento."
                      style={{ width: "100%", padding: "7px 10px", background: (spuntaRefs.length > 0 && selSpuntaRefs.size === 0) ? "#9e9e9e" : "#1b5e20", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: (spuntaRefs.length > 0 && selSpuntaRefs.size === 0) ? "not-allowed" : "pointer" }}>
                      🖨 Stampa + deleghe {selSpuntaRefs.size > 0 ? `(${selSpuntaRefs.size})` : ""}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {data?.data && (
          <button onClick={async () => {
              const [dd, mm, yyyy] = (data.data || "").split("/").map(Number);
              const annoScol = (mm && yyyy) ? (mm >= 9 ? yyyy : yyyy - 1) : null;
              try {
                const r = await api.get("/mensa-matrice/intolleranti-refettori-pdf",
                  { params: annoScol ? { anno: annoScol } : {}, responseType: "blob" });
                const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
                const w = window.open(url, "_blank");
                if (!w) {
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `intolleranti_refettori_${annoScol || ""}.pdf`;
                  document.body.appendChild(a); a.click(); a.remove();
                }
                setTimeout(() => URL.revokeObjectURL(url), 60000);
              } catch (e) {
                alert("Errore generazione PDF intolleranti per refettorio: " + (e.response?.data?.detail || e.message));
              }
            }}
            data-testid="mg-print-intoll-refettori"
            title="Stampa (A3) gli alunni con intolleranza divisi per refettorio: Gruppo 1 (Lun/Mer/Ven) e Gruppo 2 (Mar/Gio). Fonte: Matrice Mensa."
            style={{ padding: "6px 14px", background: "#ad1457", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer", display: "inline-flex", flexDirection: "column", alignItems: "center", lineHeight: 1.1 }}>
            <span>🍽 Intolleranti per refettorio (A3)</span>
            <span style={{ fontSize: 10, fontWeight: 400, opacity: 0.95 }}>Lun/Mer/Ven + Mar/Gio</span>
          </button>
        )}

        {data?.data && data?.total > 0 && (
          <button onClick={storicizza} disabled={storicizzando} data-testid="mg-storicizza-btn"
            title="Storicizza i totali alunni effettivi del giorno (Infanzia A / Primaria E+F / Secondaria M)"
            style={{ padding: "6px 14px", background: "#6a1b9a", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: storicizzando ? "wait" : "pointer" }}>
            {storicizzando ? "Storicizzazione..." : "📚 Storicizza dato"}
          </button>
        )}

        {data?.data && (
          <button onClick={handleDeleteDay} data-testid="mg-delete-day"
            style={{ padding: "6px 12px", background: "white", color: "#c62828", border: "1px solid #c62828", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
            🗑 Elimina giornata
          </button>
        )}

        <div style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "center" }}>
          <input type="text" placeholder="Cerca alunno / CF..." value={search} onChange={e => setSearch(e.target.value)}
            data-testid="mg-search" style={{ padding: "5px 10px", border: "1px solid #ccc", fontSize: 12, width: 220 }} />
          <div data-testid="mg-filter-plesso-wrapper" style={{ position: "relative" }}>
            <button type="button" data-testid="mg-filter-plesso-btn"
              onClick={() => setShowPlessoDD(s => !s)}
              title="Filtra plessi (multi-selezione, raggruppati per settore)"
              style={{ padding: "5px 10px", border: "1px solid #ccc", fontSize: 12, background: filterPlesso.size > 0 ? "#e3f2fd" : "white", color: filterPlesso.size > 0 ? "#0d47a1" : "inherit", fontWeight: filterPlesso.size > 0 ? 700 : 400, cursor: "pointer", minWidth: 150, textAlign: "left", display: "inline-flex", alignItems: "center", gap: 6 }}>
              {filterPlesso.size === 0
                ? "Tutti i plessi"
                : filterPlesso.size === 1 ? Array.from(filterPlesso)[0]
                : `${filterPlesso.size} plessi`}
              <span style={{ marginLeft: "auto", fontSize: 9 }}>{showPlessoDD ? "▲" : "▼"}</span>
            </button>
            {showPlessoDD && (
              <>
                <div onClick={() => setShowPlessoDD(false)} style={{ position: "fixed", inset: 0, zIndex: 50 }} />
                <div data-testid="mg-filter-plesso-dropdown"
                  style={{ position: "absolute", top: "calc(100% + 4px)", right: 0, background: "white", border: "1px solid #ccc", borderTop: "3px solid #1565c0", boxShadow: "0 6px 16px rgba(0,0,0,0.15)", zIndex: 60, minWidth: 280, maxHeight: 420, overflow: "auto", padding: "4px 0" }}>
                  <div style={{ display: "flex", gap: 4, padding: "4px 8px", borderBottom: "1px solid #eee", background: "#fafafa" }}>
                    <button onClick={() => setFilterPlesso(new Set(opzioniPlesso))} data-testid="mg-filter-plesso-all"
                      style={{ flex: 1, padding: "3px 8px", fontSize: 10, fontWeight: 700, background: "white", border: "1px solid #1565c0", color: "#1565c0", cursor: "pointer" }}>Tutti</button>
                    <button onClick={() => setFilterPlesso(new Set())} data-testid="mg-filter-plesso-none"
                      style={{ flex: 1, padding: "3px 8px", fontSize: 10, fontWeight: 700, background: "white", border: "1px solid #c62828", color: "#c62828", cursor: "pointer" }}>Nessuno</button>
                  </div>
                  {plessiPerSettore.map(grp => {
                    const allSelected = grp.plessi.every(p => filterPlesso.has(p));
                    const someSelected = grp.plessi.some(p => filterPlesso.has(p));
                    const toggleGroup = () => {
                      setFilterPlesso(prev => {
                        const next = new Set(prev);
                        if (allSelected) grp.plessi.forEach(p => next.delete(p));
                        else grp.plessi.forEach(p => next.add(p));
                        return next;
                      });
                    };
                    const togglePlesso = (p) => {
                      setFilterPlesso(prev => {
                        const next = new Set(prev);
                        if (next.has(p)) next.delete(p); else next.add(p);
                        return next;
                      });
                    };
                    return (
                      <div key={grp.settore} style={{ borderBottom: "1px solid #f0f0f0", paddingBottom: 4 }}>
                        <div onClick={toggleGroup} data-testid={`mg-filter-plesso-grp-${grp.settore}`}
                          style={{ padding: "5px 10px", background: allSelected ? "#1565c0" : (someSelected ? "#bbdefb" : "#f5f5f5"), color: allSelected ? "white" : "#0d47a1", fontWeight: 700, fontSize: 11, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                          <input type="checkbox"
                            checked={allSelected}
                            ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                            onChange={toggleGroup}
                            onClick={e => e.stopPropagation()}
                            style={{ cursor: "pointer" }} />
                          {grp.label}
                          <span style={{ marginLeft: "auto", fontSize: 10, fontWeight: 400, opacity: 0.85 }}>
                            {grp.plessi.filter(p => filterPlesso.has(p)).length}/{grp.plessi.length}
                          </span>
                        </div>
                        {grp.plessi.map(p => (
                          <label key={p} data-testid={`mg-filter-plesso-${p}`}
                            style={{ display: "flex", alignItems: "center", gap: 8, padding: "3px 28px", fontSize: 11, cursor: "pointer", color: filterPlesso.has(p) ? "#0d47a1" : "#444", fontWeight: filterPlesso.has(p) ? 700 : 400 }}
                            onMouseEnter={e => e.currentTarget.style.background = "#f0f6ff"}
                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                            <input type="checkbox" checked={filterPlesso.has(p)} onChange={() => togglePlesso(p)} style={{ cursor: "pointer" }} />
                            <span style={{ fontFamily: "Consolas, monospace" }}>{p}</span>
                          </label>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
          <select value={filterClasse} onChange={e => setFilterClasse(e.target.value)} data-testid="mg-filter-classe"
            style={{ padding: "5px 8px", border: "1px solid #ccc", fontSize: 12 }}>
            <option value="">Tutte le classi</option>
            {opzioniClasse.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={filterDieta} onChange={e => setFilterDieta(e.target.value)} data-testid="mg-filter-dieta"
            style={{ padding: "5px 8px", border: "1px solid #ccc", fontSize: 12 }}>
            <option value="">Tutte le diete</option>
            {opzioniDieta.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={filterRefettorio} onChange={e => setFilterRefettorio(e.target.value)} data-testid="mg-filter-refettorio"
            title="Filtra per REFETTORIO calcolato dall'elaborazione (giorno + plesso + motivo)"
            style={{ padding: "5px 8px", border: "1px solid #ccc", fontSize: 12, background: filterRefettorio ? "#e8eaf6" : "white", color: filterRefettorio ? "#1a237e" : "inherit", fontWeight: filterRefettorio ? 700 : 400 }}>
            <option value="">Tutti i refettori (da elab.)</option>
            {opzioniRefettorio.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      </div>

      {err && (
        <div style={{ margin: "0 20px 8px", padding: 10, background: "#fff0f0", border: "1px solid #fcc", color: "#c00", fontSize: 12 }} data-testid="mg-error">{err}</div>
      )}

      {importReport && (
        <div style={{ margin: "0 20px 8px", padding: 10, background: "#e8f5e9", border: "1px solid #a5d6a7", color: "#1b5e20", fontSize: 12 }} data-testid="mg-import-report">
          <b>Import {importReport.slot ? `· ${importReport.slot}` : ""} completato:</b>
          {importReport.deleted_before > 0 && <> turno ricreato: <b style={{ color: "#ef6c00" }}>{importReport.deleted_before}</b> righe sostituite · </>}
          inseriti <b>{importReport.inserted}</b>, aggiornati <b>{importReport.updated}</b>
          {importReport.errors_count > 0 && <> · errori: <b style={{ color: "#c62828" }}>{importReport.errors_count}</b></>}
          {importReport.dates?.length > 0 && <> · date: {importReport.dates.map(d => isoToDmy(d)).join(", ")}</>}
          <button onClick={() => setImportReport(null)} style={{ marginLeft: 10, background: "none", border: "none", color: "#1b5e20", cursor: "pointer", fontSize: 14, fontWeight: 700 }}>✕</button>
        </div>
      )}

      {data?.data && (
        <div data-testid="mg-slot-bar" style={{ margin: "0 20px 10px", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", padding: "8px 10px", background: "#eceff1", border: "1px solid #cfd8dc", borderRadius: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: "#37474f" }}>RILEVAZIONE:</span>
          {["Ore 10", "Ore 12"].map(s => {
            const sum = (slotSummary || []).find(x => x.slot === s) || { total: 0, importato: false };
            const active = curSlot === s;
            return (
              <button key={s} onClick={() => cambiaSlot(s)} data-testid={`mg-slot-${s === "Ore 10" ? "10" : "12"}`}
                title={sum.importato ? `${sum.total} iscritti nella rilevazione${sum.filename ? " · " + sum.filename : ""}` : "Nessuna importazione per questa rilevazione"}
                style={{ padding: "6px 14px", borderRadius: 20, border: active ? "2px solid #0d47a1" : "1px solid #b0bec5",
                  background: active ? "#1565c0" : "white", color: active ? "white" : "#37474f", fontSize: 12, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8 }}>
                {s}
                <span style={{ background: active ? "rgba(255,255,255,0.25)" : (sum.importato ? "#2e7d32" : "#cfd8dc"), color: active || sum.importato ? "white" : "#607d8b", borderRadius: 10, padding: "1px 8px", fontSize: 11, fontWeight: 800 }}>
                  {sum.importato ? sum.total : "—"}
                </span>
              </button>
            );
          })}
          <button onClick={apriConfronto} data-testid="mg-confronto-btn"
            title="Confronta le due rilevazioni: chi si è iscritto o tolto fra le 10 e le 12"
            style={{ marginLeft: 6, padding: "6px 14px", background: "#6a1b9a", color: "white", border: "none", borderRadius: 4, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            ⇄ Differenze fra le 10 e le 12
          </button>
        </div>
      )}

      {data && (
        <div style={{ margin: "0 20px 10px", display: "flex", gap: 10, flexWrap: "wrap" }}>
          <StatBox label={`TOTALE PRESENTI · ${curSlot}`} value={data.total} color="#1565c0" testid="mg-stat-total" />
          {Object.entries(data.stats?.per_plesso || {}).map(([p, n]) => (
            <StatBox key={p} label={p} value={n} color="#2e7d32" small />
          ))}
        </div>
      )}

      <div style={{ margin: "0 20px 20px", background: "white", border: "1px solid #ccc" }}>
        <div style={{ maxHeight: "65vh", overflow: "auto" }}>
          <table className="sw-table sw-table-compact" style={{ fontSize: 11 }}>
            <thead style={{ position: "sticky", top: 0, background: "#1f3864", color: "white", zIndex: 2 }}>
              <tr>
                <th style={{ width: 40, textAlign: "center" }}>#</th>
                <th style={{ minWidth: 110 }}>PLESSO</th>
                <th style={{ width: 45, textAlign: "center" }}>SET</th>
                <th style={{ minWidth: 80 }}>CLASSE</th>
                <th style={{ minWidth: 220 }}>STUDENTE</th>
                <th style={{ minWidth: 140 }}>CODICE FISCALE</th>
                <th style={{ minWidth: 200 }} title="Motivo per cui l'alunno è in mensa">MOTIVO</th>
                <th style={{ minWidth: 160 }} title="Refettorio calcolato in base a giorno + plesso + motivo">REFETTORIO (da elab.)</th>
                <th style={{ minWidth: 120 }}>DIETA / INTOLLERANZA</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} style={{ padding: 30, textAlign: "center", color: "#888" }}>Caricamento...</td></tr>
              )}
              {!loading && grouped.length === 0 && (
                <tr><td colSpan={9} style={{ padding: 30, textAlign: "center", color: "#888" }}>
                  {data?.total > 0 ? "Nessun risultato con i filtri attivi." : "Nessuna presenza per la data selezionata. Usa il bottone 📥 Importa Excel per caricarle."}
                </td></tr>
              )}
              {!loading && (() => {
                let n = 0;
                return grouped.map((it, idx) => {
                  if (it.type === "classe_sub") {
                    return (
                      <tr key={`cs-${idx}`} data-testid={`mg-sub-classe-${idx}`} style={{ background: "#fff3e0", fontWeight: 700, color: "#e65100", borderTop: "1px solid #ffb74d" }}>
                        <td colSpan={3}></td>
                        <td colSpan={2} style={{ textAlign: "right", padding: "4px 8px" }}>Subtotale classe {it.classe}</td>
                        <td style={{ textAlign: "center", fontFamily: "Consolas, monospace" }}>{it.count}</td>
                        <td colSpan={3}></td>
                      </tr>
                    );
                  }
                  if (it.type === "plesso_sub") {
                    return (
                      <tr key={`ps-${idx}`} data-testid={`mg-sub-plesso-${idx}`} style={{ background: "#e3f2fd", fontWeight: 800, color: "#0d47a1", borderTop: "2px solid #1565c0", borderBottom: "2px solid #1565c0" }}>
                        <td></td>
                        <td colSpan={4} style={{ textAlign: "right", padding: "5px 8px" }}>SUBTOTALE PLESSO {it.plesso}</td>
                        <td style={{ textAlign: "center", fontFamily: "Consolas, monospace", fontSize: 13 }}>{it.count}</td>
                        <td colSpan={3}></td>
                      </tr>
                    );
                  }
                  const r = it.r;
                  n += 1;
                  const mot = r.motivi || "";
                  const isVirt = r.refettorio_calc === "PRIMARIA EXTRA" || r.refettorio_calc === "PRIMARIA RICHIESTA";
                  return (
                    <tr key={`${r.data_iso}-${r.codice_fiscale}`} data-testid={`mg-row-${n - 1}`}>
                      <td style={{ textAlign: "center", color: "#888", fontFamily: "Consolas, monospace" }}>{n}</td>
                      <td style={{ fontFamily: "Consolas, monospace", fontSize: 10 }}>{r.plesso}</td>
                      <td style={{ textAlign: "center", fontFamily: "Consolas, monospace", fontWeight: 700, color: r.settore ? "#0d47a1" : "#ccc" }}>{r.settore || "—"}</td>
                      <td>{r.classe_desc}</td>
                      <td style={{ fontWeight: 600 }}>{r.studente}</td>
                      <td style={{ fontFamily: "Consolas, monospace", fontSize: 10 }}>{r.codice_fiscale}</td>
                      <td title={r.motivi_dettaglio || ""} style={{ fontSize: 10 }}>
                        {mot.includes("s") && <span style={{ display: "inline-block", padding: "1px 6px", background: "#e8f5e9", color: "#2e7d32", fontWeight: 700, marginRight: 3 }}>🏫 SCUOLA</span>}
                        {mot.includes("x") && <span style={{ display: "inline-block", padding: "1px 6px", background: "#e3f2fd", color: "#1565c0", fontWeight: 700, marginRight: 3 }}>✨ EXTRA</span>}
                        {mot.includes("f") && <span style={{ display: "inline-block", padding: "1px 6px", background: "#f3e5f5", color: "#6a1b9a", fontWeight: 700, marginRight: 3 }}>👨‍👩‍👧 FAM</span>}
                        {mot === "?" && <span style={{ color: "#c62828", fontWeight: 700 }} title="Alunno presente in mensa ma non previsto dalla matrice teorica">⚠ NON PREV.</span>}
                      </td>
                      <td style={{ fontSize: 10, fontWeight: 700, fontFamily: "Consolas, monospace", color: isVirt ? "#ef6c00" : (r.refettorio_calc ? "#1a237e" : "#ccc"), background: isVirt ? "#fff3e0" : (r.refettorio_calc ? "#e8eaf6" : "transparent") }}>
                        {isVirt && "✨ "}{r.refettorio_calc || "—"}
                      </td>
                      <td style={{ padding: "2px 4px", background: r.dieta ? (r.dieta_prev_year ? "#ffebee" : "#fff3e0") : "transparent" }}
                          title={r.dieta_prev_year ? "⚠ Dieta dell'anno scolastico precedente — certificato non ancora rinnovato per l'anno corrente" : undefined}>
                        <div style={{ display: "flex", gap: 3, alignItems: "center" }}>
                          <select value={r.dieta || ""}
                            onChange={e => saveDietaCella(r, e.target.value)}
                            data-testid={`mg-dieta-sel-${r.codice_fiscale}`}
                            title="Cambia dieta/intolleranza inline"
                            style={{ flex: 1, padding: "2px 4px", fontSize: 11, fontWeight: r.dieta ? 700 : 400,
                                     color: r.dieta ? (r.dieta_prev_year ? "#c62828" : "#ef6c00") : "#888",
                                     border: r.dieta ? (r.dieta_prev_year ? "1px solid #c62828" : "1px solid #ef6c00") : "1px solid #ccc",
                                     background: r.dieta ? (r.dieta_prev_year ? "#ffebee" : "#fff3e0") : "white",
                                     borderRadius: 2, cursor: "pointer" }}>
                            <option value="">— nessuna —</option>
                            {dieteList.map(d => <option key={d} value={d}>{d}</option>)}
                            {r.dieta && !dieteList.includes(r.dieta) && (
                              <option value={r.dieta}>{r.dieta} (custom)</option>
                            )}
                          </select>
                          {r.dieta_prev_year && (
                            <span data-testid={`mg-dieta-prev-badge-${r.codice_fiscale}`}
                              title="Dieta dell'anno precedente — certificato non ancora aggiornato"
                              style={{ background: "#c62828", color: "white", fontSize: 9, fontWeight: 900, padding: "1px 4px", borderRadius: 2, whiteSpace: "nowrap" }}>
                              ⚠ A.P.
                            </span>
                          )}
                          <button onClick={() => setDieteModalOpen(true)}
                            data-testid={`mg-dieta-manage-${r.codice_fiscale}`}
                            title="Gestisci elenco diete"
                            style={{ background: "#1a237e", color: "white", border: "none", padding: "2px 4px", fontSize: 10, cursor: "pointer", borderRadius: 2 }}>⚙</button>
                        </div>
                      </td>
                    </tr>
                  );
                });
              })()}
            </tbody>
            {filtered.length > 0 && (
              <tfoot>
                <tr style={{ background: "#263238", color: "white", fontWeight: 700 }}>
                  <td colSpan={7} style={{ textAlign: "right", padding: "6px 8px" }}>TOTALE VISUALIZZATI</td>
                  <td colSpan={2} style={{ fontFamily: "Consolas, monospace" }}>{filtered.length} / {data?.total || 0}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
      {dieteModalOpen && (
        <DieteManagerModal items={dieteList}
          onSaved={(newList) => setDieteList(newList)}
          onClose={() => setDieteModalOpen(false)} />
      )}

      {showOrdinaDeleghe && (
        <div data-testid="mg-ordina-deleghe-modal"
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setShowOrdinaDeleghe(false)}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: "white", borderRadius: 8, padding: 24, width: 460, maxWidth: "92vw", boxShadow: "0 12px 40px rgba(0,0,0,0.3)" }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#1b5e20", marginBottom: 4 }}>🖨 Stampa + deleghe</div>
            <div style={{ fontSize: 13, color: "#444", marginBottom: 18 }}>Come vuoi ordinare il tabulato?</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button data-testid="mg-ordina-settore-classe"
                onClick={() => { setShowOrdinaDeleghe(false); stampaElenchiSpunta(true, "settore_classe", saltoPagina); }}
                style={{ padding: "13px 16px", background: "#1b5e20", color: "white", border: "none", borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: "pointer", textAlign: "left" }}>
                1 &nbsp;·&nbsp; Settore &nbsp;·&nbsp; Classe &nbsp;·&nbsp; Cognome e Nome
              </button>
              <label data-testid="mg-salto-pagina-label"
                style={{ display: "flex", alignItems: "center", gap: 8, margin: "-2px 2px 2px", fontSize: 12.5, color: "#1b5e20", cursor: "pointer", userSelect: "none" }}>
                <input type="checkbox" data-testid="mg-salto-pagina-checkbox"
                  checked={saltoPagina} onChange={e => setSaltoPagina(e.target.checked)} />
                Salto pagina ad ogni Settore/Classe (una pagina per classe)
              </label>
              <button data-testid="mg-ordina-cognome"
                onClick={() => { setShowOrdinaDeleghe(false); stampaElenchiSpunta(true, "cognome"); }}
                style={{ padding: "13px 16px", background: "#0d47a1", color: "white", border: "none", borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: "pointer", textAlign: "left" }}>
                2 &nbsp;·&nbsp; Cognome e Nome
              </button>
            </div>
            <div style={{ marginTop: 16, textAlign: "right" }}>
              <button data-testid="mg-ordina-annulla" onClick={() => setShowOrdinaDeleghe(false)}
                style={{ padding: "8px 16px", background: "white", color: "#555", border: "1px solid #ccc", borderRadius: 6, fontSize: 13, cursor: "pointer" }}>
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}

      {showOrdinaSpunta && (
        <div data-testid="mg-ordina-spunta-modal"
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setShowOrdinaSpunta(false)}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: "white", borderRadius: 8, padding: 24, width: 460, maxWidth: "92vw", boxShadow: "0 12px 40px rgba(0,0,0,0.3)" }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#0d47a1", marginBottom: 4 }}>🖨 Stampa spunta</div>
            <div style={{ fontSize: 13, color: "#444", marginBottom: 18 }}>Come vuoi ordinare il tabulato?</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button data-testid="mg-ordina-spunta-settore-classe"
                onClick={() => { setShowOrdinaSpunta(false); stampaElenchiSpunta(false, "settore_classe", saltoPagina); }}
                style={{ padding: "13px 16px", background: "#1b5e20", color: "white", border: "none", borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: "pointer", textAlign: "left" }}>
                1 &nbsp;·&nbsp; Settore &nbsp;·&nbsp; Classe &nbsp;·&nbsp; Cognome e Nome
              </button>
              <label data-testid="mg-ordina-spunta-salto-label"
                style={{ display: "flex", alignItems: "center", gap: 8, margin: "-2px 2px 2px", fontSize: 12.5, color: "#1b5e20", cursor: "pointer", userSelect: "none" }}>
                <input type="checkbox" data-testid="mg-ordina-spunta-salto-checkbox"
                  checked={saltoPagina} onChange={e => setSaltoPagina(e.target.checked)} />
                Salto pagina ad ogni Settore/Classe (una pagina per classe)
              </label>
              <button data-testid="mg-ordina-spunta-cognome"
                onClick={() => { setShowOrdinaSpunta(false); stampaElenchiSpunta(false, "cognome"); }}
                style={{ padding: "13px 16px", background: "#0d47a1", color: "white", border: "none", borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: "pointer", textAlign: "left" }}>
                2 &nbsp;·&nbsp; Cognome e Nome
              </button>
            </div>
            <div style={{ marginTop: 16, textAlign: "right" }}>
              <button data-testid="mg-ordina-spunta-annulla" onClick={() => setShowOrdinaSpunta(false)}
                style={{ padding: "8px 16px", background: "white", color: "#555", border: "1px solid #ccc", borderRadius: 6, fontSize: 13, cursor: "pointer" }}>
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}

      {showConfronto && confronto && (
        <div data-testid="mg-confronto-modal" onClick={() => setShowConfronto(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 950, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "white", width: "100%", maxWidth: 820, maxHeight: "90vh", overflowY: "auto", borderRadius: 8 }}>
            <div style={{ background: "#6a1b9a", color: "white", padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", position: "sticky", top: 0 }}>
              <strong>⇄ Differenze fra le 10 e le 12 — {confronto.data}</strong>
              <button onClick={() => setShowConfronto(false)} data-testid="mg-confronto-close" style={{ background: "none", border: "none", color: "white", cursor: "pointer", fontSize: 20 }}>✕</button>
            </div>
            <div style={{ padding: 18 }}>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
                <StatBox label="ORE 10" value={confronto.tot_ore10} color="#1565c0" />
                <StatBox label="ORE 12" value={confronto.tot_ore12} color="#2e7d32" />
                <StatBox label="VARIAZIONE" value={`${confronto.delta >= 0 ? "+" : ""}${confronto.delta}`} color={confronto.delta === 0 ? "#607d8b" : (confronto.delta > 0 ? "#2e7d32" : "#c62828")} />
              </div>

              <div style={{ fontSize: 13, fontWeight: 800, color: "#455a64", margin: "4px 0 6px" }}>Variazione per plesso</div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginBottom: 16 }}>
                <thead><tr style={{ background: "#f5f5f5" }}>
                  <th style={{ textAlign: "left", padding: "5px 8px", border: "1px solid #e0e0e0" }}>Plesso</th>
                  <th style={{ padding: "5px 8px", border: "1px solid #e0e0e0" }}>Ore 10</th>
                  <th style={{ padding: "5px 8px", border: "1px solid #e0e0e0" }}>Ore 12</th>
                  <th style={{ padding: "5px 8px", border: "1px solid #e0e0e0" }}>Δ</th>
                </tr></thead>
                <tbody>
                  {(confronto.per_plesso || []).map(p => (
                    <tr key={p.plesso}>
                      <td style={{ padding: "5px 8px", border: "1px solid #eee", fontFamily: "Consolas, monospace" }}>{p.plesso}</td>
                      <td style={{ padding: "5px 8px", border: "1px solid #eee", textAlign: "center" }}>{p.ore10}</td>
                      <td style={{ padding: "5px 8px", border: "1px solid #eee", textAlign: "center" }}>{p.ore12}</td>
                      <td style={{ padding: "5px 8px", border: "1px solid #eee", textAlign: "center", fontWeight: 700, color: p.delta === 0 ? "#607d8b" : (p.delta > 0 ? "#2e7d32" : "#c62828") }}>{p.delta >= 0 ? "+" : ""}{p.delta}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 300 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#c62828", marginBottom: 6 }}>➖ Tolti dopo le 10 ({confronto.solo_ore10.length}) <span style={{ fontWeight: 400, color: "#999" }}>— c'erano alle 10, non più alle 12</span></div>
                  {confronto.solo_ore10.length === 0 ? <div style={{ color: "#999", fontSize: 12 }}>—</div> : (
                    <div data-testid="mg-confronto-solo10" style={{ maxHeight: 220, overflowY: "auto", border: "1px solid #eee", borderRadius: 4 }}>
                      {confronto.solo_ore10.map((r, i) => (
                        <div key={i} style={{ padding: "4px 8px", borderBottom: "1px solid #f3f3f3", fontSize: 12 }}>
                          <b>{r.studente}</b> <span style={{ color: "#888" }}>· {r.plesso} {r.classe_desc}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 300 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#2e7d32", marginBottom: 6 }}>➕ Iscritti fra le 10 e le 12 ({confronto.solo_ore12.length}) <span style={{ fontWeight: 400, color: "#999" }}>— nuovi, non c'erano alle 10</span></div>
                  {confronto.solo_ore12.length === 0 ? <div style={{ color: "#999", fontSize: 12 }}>—</div> : (
                    <div data-testid="mg-confronto-solo12" style={{ maxHeight: 220, overflowY: "auto", border: "1px solid #eee", borderRadius: 4 }}>
                      {confronto.solo_ore12.map((r, i) => (
                        <div key={i} style={{ padding: "4px 8px", borderBottom: "1px solid #f3f3f3", fontSize: 12 }}>
                          <b>{r.studente}</b> <span style={{ color: "#888" }}>· {r.plesso} {r.classe_desc}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// (DieteManagerModal è ora in /components/DieteManagerModal.jsx — condiviso con MensaMatricePage)

function StatBox({ label, value, color, small, testid }) {
  return (
    <div data-testid={testid}
      style={{ padding: small ? "6px 12px" : "8px 16px", background: "white", border: `2px solid ${color}`, borderLeft: `6px solid ${color}`, minWidth: small ? 100 : 140 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: "#666", textTransform: "uppercase", letterSpacing: 0.3 }}>{label}</div>
      <div style={{ fontSize: small ? 16 : 22, fontWeight: 800, color, fontFamily: "Consolas, monospace" }}>{value}</div>
    </div>
  );
}
